<template>
  <div :data-cy="`dataset-upload-${dataset.name}`">
    <p v-if="label" class="mb-2 text-sm font-medium text-gray-900">
      {{ label }}
    </p>
    <div
      v-if="file"
      class="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
      data-cy="uploaded-file"
    >
      <i class="fas fa-file-excel text-xl text-gray-400" aria-hidden="true" />
      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-medium text-gray-900">
          {{ file.name }}
        </p>
        <div
          v-if="!uploaded"
          class="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-200"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="progress"
          data-cy="upload-progress"
        >
          <div
            class="h-full rounded-full bg-blue-600"
            :style="{ width: `${progress}%` }"
          />
        </div>
        <p v-else class="text-xs text-gray-500">{{ sizeLabel }}</p>
      </div>
      <button
        type="button"
        class="shrink-0 text-sm text-gray-500 hover:text-red-600"
        :aria-label="uploaded ? 'Remove file' : 'Cancel upload'"
        data-cy="remove-file"
        @click="clear"
      >
        <i class="fas fa-times" aria-hidden="true" />
      </button>
    </div>
    <FileDropzone
      v-else
      :accept="dataset.extensions"
      :hint="hint"
      :error="uploadError"
      @file="upload"
    />
  </div>
</template>

<script setup lang="ts">
import API from "serviceshift-ui/api-client";
import { computed, onBeforeUnmount, ref } from "vue";
import { useStore } from "vuex";

import FileDropzone from "./FileDropzone.vue";
import type { VendorDataset } from "./types";

const props = defineProps<{
  dataset: VendorDataset;
  /** `links.file_upload` of the selected vendor (presign endpoint). */
  fileUploadLink: string;
  label?: string;
  hint?: string;
  /** Files above this are refused before anything is uploaded. */
  maxBytes?: number;
}>();

const emit = defineEmits<{
  uploaded: [key: string];
  cleared: [];
  busy: [busy: boolean];
}>();

const MB = 1024 * 1024;

const store = useStore();
const file = ref<File | null>(null);
const progress = ref(0);
const uploaded = ref(false);
const uploadError = ref("");

// Each pick bumps the attempt; a slower, abandoned upload must not touch the state of a newer one.
let attempt = 0;
let cancelSource: { cancel: (reason?: string) => void } | null = null;

const sizeLabel = computed(() => {
  const size = file.value?.size ?? 0;
  return size >= MB
    ? `${(size / MB).toFixed(1)} MB`
    : `${Math.ceil(size / 1024)} KB`;
});

function errorMessage(error: any): string {
  return error?.response?.data?.message || "Upload failed. Try again.";
}

async function upload(chosen: File) {
  uploadError.value = "";
  if (props.maxBytes && chosen.size > props.maxBytes) {
    uploadError.value = `File is over ${Math.round(props.maxBytes / MB)} MB`;
    return;
  }

  const id = ++attempt;
  file.value = chosen;
  progress.value = 0;
  uploaded.value = false;
  emit("busy", true);
  const source = API.axios.CancelToken.source();
  cancelSource = source;

  try {
    const presigned = await store.dispatch("getS3UploadURL", {
      link: props.fileUploadLink,
      filename: chosen.name,
      ignoreError: true
    });
    const key = await store.dispatch("s3Upload", {
      data: presigned.data,
      file: chosen,
      body: {
        onUploadProgress: (event: { loaded: number; total?: number }) => {
          if (id === attempt && event.total) {
            progress.value = Math.round((event.loaded / event.total) * 100);
          }
        },
        cancelToken: source.token
      }
    });
    if (id !== attempt) return;
    progress.value = 100;
    uploaded.value = true;
    emit("uploaded", key);
  } catch (error) {
    if (id !== attempt) return;
    file.value = null;
    uploadError.value = errorMessage(error);
  } finally {
    if (id === attempt) emit("busy", false);
  }
}

function clear() {
  attempt += 1;
  cancelSource?.cancel("Upload canceled");
  cancelSource = null;
  file.value = null;
  progress.value = 0;
  uploaded.value = false;
  emit("busy", false);
  emit("cleared");
}

onBeforeUnmount(() => {
  attempt += 1;
  cancelSource?.cancel("Upload canceled");
});
</script>
